// src/pages/Consolidations.jsx
// Phase R8 — Group Consolidations & Multi-entity Engine
// Aggregate multiple group entities, detect intercompany eliminations with AI,
// apply ownership %, compute minority interest, generate consolidated IFRS AFS.

import { useState, useEffect, useMemo, useRef } from 'react'
import { Construction } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import BackButton from '../components/BackButton'
import {
  listConsolidations, createConsolidation, getConsolidation,
  updateConsolidation, detectIntercompany, generateConsolidated,
  getClients, getAFSJobs, getEliminationHistory, updateEliminationHistoryYear,
} from '../api/kailux'
import Spinner from '../components/Spinner'
import { pointsToBilling } from '../utils/lockMessage'

import Icon from '../components/Icon'

import { T, alpha } from '../theme/theme'
// ── Elimination type presets ───────────────────────────────────────────────
const ELIM_TYPES = [
  { id:'intercompany_loan',    label:'Intercompany Loan',          dr:'Loans from Shareholders',    cr:'Trade and Other Receivables',  desc:'Loan balance between group entities' },
  { id:'intercompany_trade',   label:'Intercompany Trade Balance',  dr:'Trade and Other Payables',   cr:'Trade and Other Receivables',  desc:'Outstanding intercompany trade balance' },
  { id:'management_fee',       label:'Management Fee',             dr:'Revenue',                    cr:'Professional Fees',            desc:'Management fees charged between entities' },
  { id:'investment',           label:'Investment Elimination',     dr:'Stated Capital',             cr:'Intangible Assets',            desc:'Cost of investment vs subsidiary equity' },
  { id:'intercompany_revenue', label:'Intercompany Revenue',       dr:'Revenue',                    cr:'General and Admin Expenses',   desc:'Revenue in one entity = expense in another' },
  { id:'dividend',             label:'Intercompany Dividend',      dr:'Other Income',               cr:'Retained Earnings',            desc:'Dividend declared between group entities' },
  { id:'custom',               label:'Custom',                     dr:'',                           cr:'',                             desc:'Manual elimination entry' },
]

const IFRS_CATS = [
  'Trade and Other Receivables','Loans from Shareholders','Borrowings - Non-Current',
  'Borrowings - Current','Trade and Other Payables','Stated Capital','Retained Earnings',
  'Revenue','Other Income','Professional Fees','General and Admin Expenses',
  'Employee Costs','Finance Costs','Intangible Assets','Property Plant & Equipment',
  'Cash and Cash Equivalents','Inventories','Deferred Tax Liability','Deferred Tax Asset',
  'Accumulated Depreciation','Depreciation and Amortisation','Cost of Sales',
  'Occupancy Costs','Travel and Motor Expenses','Communication Expenses',
]

const KEY_ROWS_INCOME = [
  { key:'Revenue',                       label:'Revenue',             bold:false },
  { key:'Cost of Sales',                 label:'Cost of Sales',       bold:false },
  { key:'_gross',                        label:'Gross Profit',        bold:true, computed:true },
  { key:'Employee Costs',                label:'Employee Costs',      bold:false },
  { key:'Depreciation and Amortisation', label:'Depreciation',        bold:false },
  { key:'Finance Costs',                 label:'Finance Costs',       bold:false },
  { key:'Income Tax Expense',            label:'Income Tax',          bold:false },
  { key:'_pat',                          label:'Profit After Tax',    bold:true, computed:true },
]

const KEY_ROWS_BALANCE = [
  { key:'_totalAssets',  label:'Total Assets',     bold:true, computed:true },
  { key:'_totalEquity',  label:'Total Equity',     bold:true, computed:true },
  { key:'_totalLiab',   label:'Total Liabilities', bold:true, computed:true },
]

const STEPS = ['setup','subsidiaries','eliminations','preview']
const STEP_LABELS = { setup:'Group Setup', subsidiaries:'Entities', eliminations:'Eliminations', preview:'Preview & Generate' }

// ── Helpers ────────────────────────────────────────────────────────────────
export function fmtR(n) {
  if (n === null || n === undefined || n === 0) return '—'
  const abs = Math.abs(n)
  let s
  if (abs >= 1_000_000)  s = `R${(abs/1_000_000).toFixed(2)}m`
  else if (abs >= 1_000) s = `R${Math.round(abs/1_000)}k`
  else                   s = `R${Math.round(abs)}`
  return n < 0 ? `(${s})` : s
}

export function computeConsolidated(parentFigs, subsWithFigs, eliminations) {
  const result = {}
  // Aggregate parent (always 100%)
  Object.entries(parentFigs || {}).forEach(([k, v]) => {
    result[k] = (result[k] || 0) + (parseFloat(v) || 0)
  })
  // Aggregate subsidiaries by ownership %
  subsWithFigs.forEach(sub => {
    const pct = (parseFloat(sub.ownership_pct) || 100) / 100
    Object.entries(sub.figures || {}).forEach(([k, v]) => {
      result[k] = (result[k] || 0) + (parseFloat(v) || 0) * pct
    })
  })
  // Apply eliminations (reduce both sides).
  // REVERT (5 Sep 2026, later same day): a QA pass earlier today "fixed"
  // this based on two stale, corrupted afs_jobs records that had inverted
  // signs on their credit-nature figures. Real figures are accumulated as
  // "credit - debit" (see backend/main.py's _CREDIT_NATURE_CATEGORIES
  // comment), so a normal credit balance (Revenue, Payables, Borrowings) is
  // POSITIVE. The since-reverted "fix" silently zeroed out any correctly
  // -signed positive credit-nature figure on the first elimination
  // touching it. Plain subtraction (matching the backend) is correct.
  eliminations.forEach(elim => {
    const amt = parseFloat(elim.amount) || 0
    if (elim.dr_category) result[elim.dr_category] = (result[elim.dr_category] || 0) - amt
    if (elim.cr_category) result[elim.cr_category] = (result[elim.cr_category] || 0) - amt
  })
  return result
}

export function derivedFigures(figs) {
  const g = k => parseFloat(figs?.[k] || 0)
  const revenue = g('Revenue'), cogs = g('Cost of Sales')
  const gp      = revenue - cogs
  const opex    = g('Employee Costs') + g('Occupancy Costs') + g('Communication Expenses') +
    g('Travel and Motor Expenses') + g('General and Admin Expenses') + g('Professional Fees') +
    g('Depreciation and Amortisation') + g('Credit Losses') + g('Sales and Marketing')
  const pat       = gp + g('Other Income') - opex - g('Finance Costs') - g('Income Tax Expense')
  const ppe       = Math.max(g('Property Plant & Equipment') - g('Accumulated Depreciation'), 0)
  const totalAssets  = ppe + g('Intangible Assets') + g('Investments') + g('Deferred Tax Asset') +
    g('Inventories') + g('Trade and Other Receivables') + g('Tax Receivables') + g('Cash and Cash Equivalents')
  const totalEquity  = g('Stated Capital') + g('Retained Earnings') + pat
  const totalLiab    = g('Borrowings - Non-Current') + g('Loans from Shareholders') +
    g('Deferred Tax Liability') + g('Trade and Other Payables') + g('Borrowings - Current') + g('Deferred Revenue')
  return { ...figs, _gross: gp, _pat: pat, _totalAssets: totalAssets, _totalEquity: totalEquity, _totalLiab: totalLiab }
}

// ══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════
export default function Consolidations() {
  const navigate = useNavigate()
  const { firm } = useAuth()

  // Screen: 'list' | 'builder'
  const [screen, setScreen]         = useState('list')
  const [builderStep, setBuilderStep] = useState('setup')

  // List state
  const [groups, setGroups]           = useState([])
  const [listLoading, setListLoading] = useState(true)
  const [listError, setListError]     = useState('')

  // Builder state
  const [editGroupId, setEditGroupId] = useState(null)
  const [groupName, setGroupName]     = useState('')
  const [financialYear, setFinancialYear] = useState(String(new Date().getFullYear()))
  const [parentClient, setParentClient]   = useState(null)
  const [subsidiaries, setSubsidiaries]   = useState([])  // [{client, ownership_pct}]
  const [eliminations, setEliminations]   = useState([])
  // Full per-financial-year elimination snapshots for this group (see backend
  // consolidation_eliminations_history) — lets a preparer see AND edit which prior
  // years will be genuinely re-eliminated on generate versus which fall back to an
  // unadjusted comparative (feat/consolidation-prior-year-elimination-editing).
  const [eliminationHistory, setEliminationHistory] = useState([])  // [{financial_year, eliminations, updated_at}]
  const [editingHistoryYear, setEditingHistoryYear]   = useState(null)  // financial_year string, or null
  const [historyDraft, setHistoryDraft]               = useState([])   // eliminations being edited for that year
  const [historyNewElim, setHistoryNewElim]           = useState({ dr_category: IFRS_CATS[0], cr_category: IFRS_CATS[0], amount: '' })
  const [historySaving, setHistorySaving]             = useState(false)
  const [historySaveError, setHistorySaveError]       = useState('')
  const eliminationHistoryYears = eliminationHistory.map(h => h.financial_year)
  const [entityFigures, setEntityFigures] = useState({})  // {client_id: figures | null}
  const [allClients, setAllClients]       = useState([])
  const [clientSearch, setClientSearch]   = useState('')
  const [subSearch, setSubSearch]         = useState('')

  // Elimination form
  const [addingElim, setAddingElim]   = useState(false)
  const [newElim, setNewElim]         = useState({
    type_id:'intercompany_loan', description:'',
    dr_category:'Loans from Shareholders', cr_category:'Trade and Other Receivables', amount:'',
  })

  // AI detection
  const [detecting, setDetecting]     = useState(false)
  const [suggestions, setSuggestions] = useState([])
  const [detectError, setDetectError] = useState('')

  // Generation
  const [generating, setGenerating]     = useState(false)
  const [savingGroup, setSavingGroup]   = useState(false)
  const [generateError, setGenerateError] = useState('')
  const [doneGroup, setDoneGroup]         = useState(null)

  // R4 validation gate (Phase 1, 9 Sep 2026) — populated from the 422 the
  // backend now returns when the consolidated figures fail validate_figures().
  // Mirrors Statements.jsx's validation/validationOverride pattern for the
  // standalone AFS path.
  const [validationChecks, setValidationChecks]     = useState(null)
  const [validationOverride, setValidationOverride] = useState(false)
  const validationHasFail = (validationChecks || []).some(c => c.status === 'fail')
  const validationGateOpen = validationHasFail && !validationOverride

  // Phase 3 (10 Sep 2026) — non-blocking warnings (year-end mismatch, foreign
  // jurisdiction, cash flow distortion risk, elimination sanity-check) sent
  // back via the X-Consolidation-Warnings response header.
  const [postGenerateWarnings, setPostGenerateWarnings] = useState([])

  // Track newly created group ID across async calls
  const createdGroupIdRef = useRef(null)

  // ── Computed consolidated figures ────────────────────────────────────────
  const subsWithFigs = subsidiaries.map(s => ({
    ...s, figures: entityFigures[s.client?.id] || {},
  }))

  const consolidatedFigs = useMemo(() =>
    parentClient
      ? computeConsolidated(entityFigures[parentClient.id] || {}, subsWithFigs, eliminations)
      : {},
    [parentClient, entityFigures, subsidiaries, eliminations] // eslint-disable-line
  )

  const consolidatedDerived = useMemo(() => derivedFigures(consolidatedFigs), [consolidatedFigs])

  // Minority interest
  const minorityInterest = useMemo(() => {
    let mi = 0
    subsidiaries.forEach(sub => {
      if (sub.ownership_pct < 100) {
        const miPct   = (100 - sub.ownership_pct) / 100
        const subFigs = entityFigures[sub.client?.id] || {}
        mi += ((parseFloat(subFigs['Stated Capital']) || 0) + (parseFloat(subFigs['Retained Earnings']) || 0)) * miPct
      }
    })
    return Math.round(mi)
  }, [subsidiaries, entityFigures])

  // ── Load on mount ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!firm?.id) return
    loadGroups()
    getClients(firm.id).then(({ data }) => setAllClients(data || [])).catch(() => {})
  }, [firm?.id]) // eslint-disable-line

  const loadGroups = async () => {
    setListLoading(true); setListError('')
    try {
      const { data } = await listConsolidations(firm.id)
      setGroups(data?.consolidations || [])
    } catch(err) {
      const msg = err?.response?.data?.detail || ''
      if (msg.toLowerCase().includes('consolidation') || err?.response?.status === 500) {
        setListError('table_missing')
      } else {
        setListError('Could not load consolidation groups.')
      }
    } finally {
      setListLoading(false)
    }
  }

  // Load all entity figures in one shot using the firm's AFS jobs
  const loadEntityFigures = async (clientIds) => {
    if (!firm?.id || !clientIds.length) return
    try {
      const { data: allJobs } = await getAFSJobs(firm.id)
      const map = {}
      clientIds.forEach(cid => {
        const match = (allJobs || [])
          .filter(j => j.client_id === cid && j.status === 'complete')
          .sort((a,b) => (b.financial_year||'').localeCompare(a.financial_year||''))[0]
        map[cid] = match?.figures || {}
      })
      setEntityFigures(prev => ({ ...prev, ...map }))
    } catch(e) { console.error('loadEntityFigures failed (non-fatal):', e) }
  }

  useEffect(() => {
    const ids = [parentClient?.id, ...subsidiaries.map(s => s.client?.id)].filter(Boolean)
    const toLoad = ids.filter(id => entityFigures[id] === undefined)
    if (toLoad.length) loadEntityFigures(toLoad)
  }, [parentClient, subsidiaries]) // eslint-disable-line

  // ── Handlers ──────────────────────────────────────────────────────────────
  const startNewGroup = () => {
    setEditGroupId(null); createdGroupIdRef.current = null
    setGroupName(''); setFinancialYear(String(new Date().getFullYear()))
    setParentClient(null); setSubsidiaries([]); setEliminations([])
    setEntityFigures({}); setSuggestions([]); setGenerateError('')
    setClientSearch(''); setSubSearch(''); setAddingElim(false)
    setDoneGroup(null); setEliminationHistory([]); setEditingHistoryYear(null)
    setBuilderStep('setup'); setScreen('builder')
  }

  const loadEliminationHistoryYears = async (groupId) => {
    if (!groupId) return
    try {
      const { data } = await getEliminationHistory(groupId)
      setEliminationHistory(data?.history || [])
    } catch (e) { console.error('loadEliminationHistoryYears failed (non-fatal):', e) }
  }

  const startEditingHistoryYear = (year) => {
    const existing = eliminationHistory.find(h => h.financial_year === year)
    setHistoryDraft((existing?.eliminations || []).map((e, i) => ({ ...e, _k: i })))
    setHistoryNewElim({ dr_category: IFRS_CATS[0], cr_category: IFRS_CATS[0], amount: '' })
    setHistorySaveError('')
    setEditingHistoryYear(year)
  }

  const addHistoryDraftElim = () => {
    if (!historyNewElim.amount) return
    setHistoryDraft(prev => [...prev, { ...historyNewElim, _k: Date.now() }])
    setHistoryNewElim({ dr_category: IFRS_CATS[0], cr_category: IFRS_CATS[0], amount: '' })
  }

  const removeHistoryDraftElim = (k) => {
    setHistoryDraft(prev => prev.filter(e => e._k !== k))
  }

  const saveHistoryYear = async (year) => {
    const gid = editGroupId || createdGroupIdRef.current
    if (!gid) return
    setHistorySaving(true); setHistorySaveError('')
    try {
      // Strip the client-only _k React key before sending.
      const payload = historyDraft.map(({ _k, ...rest }) => rest)
      await updateEliminationHistoryYear(gid, year, payload)
      await loadEliminationHistoryYears(gid)
      setEditingHistoryYear(null)
    } catch (err) {
      setHistorySaveError(err?.response?.data?.detail || `Could not save FY ${year} eliminations.`)
    } finally {
      setHistorySaving(false)
    }
  }

  const editGroup = async (groupId) => {
    try {
      const { data } = await getConsolidation(groupId)
      setEditGroupId(groupId); createdGroupIdRef.current = groupId
      setGroupName(data.group_name || '')
      setFinancialYear(data.financial_year || String(new Date().getFullYear()))
      setEliminations(data.eliminations || [])
      setSuggestions([]); setGenerateError(''); setDoneGroup(null)
      setEditingHistoryYear(null)
      loadEliminationHistoryYears(groupId)

      // Parent
      if (data.parent_client) {
        setParentClient(data.parent_client)
        if (data.parent_figures) {
          setEntityFigures(prev => ({ ...prev, [data.parent_client.id]: data.parent_figures }))
        }
      }
      // Subsidiaries
      const subs = (data.subsidiary_data || []).map(s => {
        const clientObj = allClients.find(c => c.id === s.client_id) || {
          id: s.client_id, name: s.client_name || s.client_id,
          entity_type: s.entity_type || 'Pty Ltd', industry: '',
        }
        if (s.figures) setEntityFigures(prev => ({ ...prev, [s.client_id]: s.figures }))
        return {
          client: clientObj, ownership_pct: s.ownership_pct ?? 100,
          // Phase 2b (10 Sep 2026) — goodwill/acquisition accounting. Every
          // field here is optional; a subsidiary with none of these set
          // behaves exactly as before (common-control formation, no
          // goodwill). isAcquisition just controls whether the acquisition
          // fields are shown expanded in the UI — the actual signal the
          // backend uses is whether acquisition_date + consideration_paid
          // are both present.
          isAcquisition:          !!(s.acquisition_date || s.consideration_paid),
          acquisition_date:       s.acquisition_date || '',
          consideration_paid:     s.consideration_paid ?? '',
          fair_value_net_assets:  s.fair_value_net_assets ?? '',
          fair_value_adjustment:  s.fair_value_adjustment ?? '',
          useful_life_years:      s.useful_life_years ?? '',
        }
      })
      setSubsidiaries(subs)
      setBuilderStep('setup'); setScreen('builder')
    } catch(err) { console.error('editGroup failed:', err) }
  }

  const addSubsidiary = (client) => {
    if (!client || client.id === parentClient?.id) return
    if (subsidiaries.find(s => s.client.id === client.id)) return
    setSubsidiaries(prev => [...prev, { client, ownership_pct: 100 }])
    setSubSearch('')
  }

  const removeSubsidiary = (clientId) =>
    setSubsidiaries(prev => prev.filter(s => s.client.id !== clientId))

  const updateOwnership = (clientId, raw) => {
    const v = Math.min(100, Math.max(1, parseInt(raw) || 100))
    setSubsidiaries(prev => prev.map(s => s.client.id === clientId ? { ...s, ownership_pct: v } : s))
  }

  // Phase 2b (10 Sep 2026) — goodwill/acquisition accounting fields.
  const updateSubsidiaryField = (clientId, field, value) =>
    setSubsidiaries(prev => prev.map(s => s.client.id === clientId ? { ...s, [field]: value } : s))

  const addElimination = () => {
    const amt = parseFloat(newElim.amount)
    if (!amt || !newElim.dr_category || !newElim.cr_category) return
    const t = ELIM_TYPES.find(x => x.id === newElim.type_id)
    setEliminations(prev => [...prev, {
      id:          Math.random().toString(36).slice(2, 9),
      type:        newElim.type_id,
      description: newElim.description.trim() || t?.label || 'Elimination',
      dr_category: newElim.dr_category,
      cr_category: newElim.cr_category,
      amount:      amt,
      manual:      true,
    }])
    setNewElim({ type_id:'intercompany_loan', description:'', dr_category:'Loans from Shareholders', cr_category:'Trade and Other Receivables', amount:'' })
    setAddingElim(false)
  }

  const removeElimination = (id) => setEliminations(prev => prev.filter(e => e.id !== id))

  const acceptSuggestion = (sug) => {
    setEliminations(prev => [...prev, {
      ...sug, id: sug.id || Math.random().toString(36).slice(2,9), manual: false,
    }])
    setSuggestions(prev => prev.filter(s => s.id !== sug.id))
  }

  // Save and return group ID (handles both create and update)
  const saveGroupSilent = async () => {
    const gid = editGroupId || createdGroupIdRef.current
    setSavingGroup(true)
    try {
      const subData = subsidiaries.map(s => ({
        client_id:     s.client.id,
        client_name:   s.client.name,
        entity_type:   s.client.entity_type || 'Pty Ltd',
        ownership_pct: s.ownership_pct,
        // Phase 2b (10 Sep 2026) — only sent when the preparer has actually
        // marked this subsidiary as an acquisition; otherwise every field
        // is omitted entirely, so a subsidiary added before this feature
        // existed (or one that's simply common-control) round-trips with
        // no acquisition data at all, exactly as before.
        ...(s.isAcquisition ? {
          acquisition_date:      s.acquisition_date || null,
          consideration_paid:    s.consideration_paid !== '' ? parseFloat(s.consideration_paid) : null,
          fair_value_net_assets: s.fair_value_net_assets !== '' ? parseFloat(s.fair_value_net_assets) : null,
          fair_value_adjustment: s.fair_value_adjustment !== '' ? parseFloat(s.fair_value_adjustment) : null,
          useful_life_years:     s.useful_life_years !== '' ? parseFloat(s.useful_life_years) : null,
        } : {}),
      }))
      if (gid) {
        await updateConsolidation(gid, {
          group_name: groupName, subsidiary_data: subData,
          financial_year: financialYear, eliminations,
        })
        return gid
      } else {
        const { data } = await createConsolidation({
          firm_id: firm.id, group_name: groupName, financial_year: financialYear,
          parent_client_id: parentClient.id, subsidiary_data: subData, eliminations,
        })
        setEditGroupId(data.id)
        createdGroupIdRef.current = data.id
        return data.id
      }
    } catch(err) {
      console.error('saveGroupSilent failed:', err)
      return null
    } finally {
      setSavingGroup(false)
    }
  }

  const handleDetect = async () => {
    let gid = editGroupId || createdGroupIdRef.current
    if (!gid) { gid = await saveGroupSilent(); if (!gid) return }
    setDetecting(true); setDetectError('')
    try {
      const { data } = await detectIntercompany(gid)
      // The backend sets ai_unavailable when the AI did not answer (circuit
      // open, e.g. credits exhausted) -- say so rather than falling through
      // to "No intercompany items detected", a false all-clear.
      if (data.ai_unavailable) {
        setSuggestions([])
        setDetectError(data.message || 'AI detection is temporarily unavailable. Please add eliminations manually or try again shortly.')
        return
      }
      // The backend (§91) returns `suggestions` already in this page's shape:
      // id, a UI type id, dr/cr category names that exist in the entities'
      // figures, and a numeric amount.
      const incoming = data.suggestions || []
      const newSugs = incoming.filter(s =>
        !eliminations.find(e => e.type === s.type && Math.abs((e.amount||0) - (s.amount||0)) < 1)
      )
      setSuggestions(newSugs)
      if (newSugs.length === 0) {
        setDetectError(
          incoming.length > 0
            ? 'The detected items are already in your eliminations.'
            : (data.message || 'No intercompany items detected. Add eliminations manually if required.')
        )
      }
    } catch {
      setDetectError('AI detection failed. Please add eliminations manually.')
    } finally {
      setDetecting(false)
    }
  }

  const handleGenerate = async () => {
    if (validationGateOpen) {
      setGenerateError('Review the validation issue(s) below, then tick the override to proceed.')
      return
    }
    setGenerating(true); setGenerateError(''); setPostGenerateWarnings([])
    try {
      const gid = await saveGroupSilent()
      if (!gid) { setGenerateError('Could not save group. Please try again.'); setGenerating(false); return }
      const resp = await generateConsolidated(gid, validationOverride)
      const url  = window.URL.createObjectURL(resp.data)
      const link = document.createElement('a')
      link.href = url; link.download = `${groupName}_Consolidated_AFS_${financialYear}.pdf`
      document.body.appendChild(link); link.click()
      document.body.removeChild(link); window.URL.revokeObjectURL(url)
      // Phase 3 (10 Sep 2026): non-blocking warnings (year-end mismatch, foreign
      // jurisdiction, cash flow distortion risk, elimination sanity-check).
      // Header names arrive lowercased under axios. URL-decoded since the
      // backend encodes it for HTTP header safety (see generate_consolidated()).
      const warningsHeader = resp.headers?.['x-consolidation-warnings'] || ''
      setPostGenerateWarnings(warningsHeader ? decodeURIComponent(warningsHeader).split('; ').filter(Boolean) : [])
      setDoneGroup({ id: gid, name: groupName, year: financialYear })
      loadEliminationHistoryYears(gid)
      setValidationChecks(null); setValidationOverride(false)
      setBuilderStep('done')
    } catch(err) {
      // generateConsolidated uses responseType:'blob' (needed for the real PDF
      // success path), which means an error response body — e.g. this R4
      // validation gate's 422 — arrives as a Blob too, not parsed JSON, so
      // err?.response?.data?.detail is always undefined straight off the
      // error. Same fix pattern already established in Statements.jsx's
      // handleGenerate for the equivalent standalone-path issue.
      let detail = err?.response?.data?.detail
      if (!detail && err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text()
          detail = JSON.parse(text)?.detail
        } catch { /* not JSON — fall through to the generic message below */ }
      }
      if (detail && typeof detail === 'object' && Array.isArray(detail.checks)) {
        setValidationChecks(detail.checks)
        setGenerateError(detail.message || 'This consolidated AFS did not pass validation.')
      } else if (detail && typeof detail === 'object' && Array.isArray(detail.missing_items)) {
        // Phase 1b (9 Sep 2026) — disclosure checklist gate, proxied via the
        // parent entity's own confirmed checklist. Deliberately NOT wired
        // into the validationChecks panel/override above — like the
        // standalone path, there is no override for this; the preparer must
        // actually go confirm the checklist for the parent entity.
        setGenerateError(
          `${detail.message || 'The disclosure checklist has not been fully confirmed for the parent entity.'} `
          + `Missing: ${detail.missing_items.join(', ')}`
        )
      } else {
        setGenerateError(typeof detail === 'string' ? detail : 'PDF generation failed. Please check Railway logs.')
      }
    } finally {
      setGenerating(false)
    }
  }

  const handleStepNext = async () => {
    const idx = STEPS.indexOf(builderStep)
    if (idx < STEPS.length - 1) {
      if (builderStep === 'subsidiaries') await saveGroupSilent()
      setBuilderStep(STEPS[idx + 1])
    }
  }

  const handleStepBack = () => {
    const idx = STEPS.indexOf(builderStep)
    if (idx > 0) setBuilderStep(STEPS[idx - 1])
  }

  // R4 validation gate: a fail/override from a previous attempt is only
  // meaningful for the figures it was computed against — clear it whenever
  // the preparer changes eliminations or the target year, so a stale
  // override checkbox can't silently wave through a different, unvalidated
  // set of figures.
  useEffect(() => {
    setValidationChecks(null)
    setValidationOverride(false)
    setGenerateError('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eliminations, financialYear])

  // ── Filtered client lists ─────────────────────────────────────────────────
  const filteredParentClients = allClients.filter(c =>
    !clientSearch || c.name.toLowerCase().includes(clientSearch.toLowerCase())
  )
  const filteredSubClients = allClients.filter(c =>
    c.name.toLowerCase().includes(subSearch.toLowerCase()) &&
    c.id !== parentClient?.id &&
    !subsidiaries.find(s => s.client.id === c.id)
  )

  const canProceedSetup = groupName.trim().length > 0 && parentClient !== null
  const canProceedSubs  = subsidiaries.length > 0

  // ════════════════════════════════════════════════════════════════════════
  // RENDER — LIST SCREEN
  // ════════════════════════════════════════════════════════════════════════
  if (screen === 'list') return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'Outfit',sans-serif", color:T.tx }}>
      <div style={{ background:T.s1, borderBottom:`1px solid ${T.bd}`, padding:'0 32px',
        height:'62px', display:'flex', alignItems:'center', justifyContent:'space-between',
        position:'sticky', top:0, zIndex:40 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'16px' }}>
          <BackButton onClick={() => navigate('/dashboard')} label="Dashboard"
            textColor={T.mt} borderColor={T.bd} accentColor={T.or} />
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'18px', fontWeight:'800' }}>
            Kai<span style={{color:T.or}}>Lux</span>
          </div>
          <span style={{ fontSize:'14px', color:T.mt }}>/ Group Consolidations</span>
        </div>
        <button onClick={startNewGroup}
          style={{ padding:'9px 20px', borderRadius:'8px', border:'none',
            background:T.prg, color:T.onpri,
            fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
          + New Consolidation Group
        </button>
      </div>

      <div style={{ padding:'32px' }}>
        <div style={{ marginBottom:'28px' }}>
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'26px', fontWeight:'800',
            letterSpacing:'-0.5px', marginBottom:'6px' }}>Group Consolidations</div>
          <div style={{ fontSize:'14px', fontWeight:'400', color:T.mt }}>
            Consolidate multiple group entities into a single IFRS-compliant set of financial statements.
            Auto-detect intercompany eliminations with AI — a capability no competitor offers.
          </div>
        </div>

        {/* Table setup notice */}
        {listError === 'table_missing' && (
          <div style={{ background:alpha(T.bl, 0.08), border:`1px solid ${alpha(T.bl, 0.2)}`,
            borderRadius:'12px', padding:'20px 24px', marginBottom:'24px' }}>
            <div style={{ fontSize:'14px', fontWeight:'700', color:T.bl, marginBottom:'6px' }}>
              <Icon name="log" /> One-time database setup required
            </div>
            <div style={{ fontSize:'14px', color:T.mt, lineHeight:'1.7', marginBottom:'12px' }}>
              Run this SQL in your Supabase SQL editor (Dashboard → SQL Editor) to enable consolidations:
            </div>
            <pre style={{ padding:'12px', background:T.s2, borderRadius:'8px',
              fontSize:'12px', color:T.tx, overflowX:'auto', lineHeight:'1.7', margin:0 }}>
{`CREATE TABLE consolidation_groups (
  id                 uuid DEFAULT gen_random_uuid() PRIMARY KEY,
  firm_id            uuid REFERENCES firms(id),
  group_name         text NOT NULL,
  parent_client_id   uuid REFERENCES clients(id),
  subsidiary_data    jsonb DEFAULT '[]',
  financial_year     text,
  eliminations       jsonb DEFAULT '[]',
  status             text DEFAULT 'draft',
  last_generated_at  timestamptz,
  created_at         timestamptz DEFAULT now()
);`}
            </pre>
            <div style={{ marginTop:'12px', fontSize:'12px', color:T.mt }}>
              After running, refresh this page.
            </div>
          </div>
        )}

        {listLoading ? (
          <div style={{ textAlign:'center', padding:'60px' }}><Spinner/></div>
        ) : groups.length === 0 && listError !== 'table_missing' ? (
          <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'16px',
            padding:'56px 40px', textAlign:'center' }}>
            <div style={{ marginBottom:'20px' }}><Construction size={48} strokeWidth={1.5} color={T.mt}/></div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px', fontWeight:'800', marginBottom:'8px' }}>
              No consolidation groups yet
            </div>
            <div style={{ fontSize:'14px', fontWeight:'400', color:T.lt, lineHeight:'1.8', marginBottom:'28px',
              maxWidth:'500px', margin:'0 auto 28px' }}>
              Group consolidations combine multiple entities — holding company, operating subsidiaries,
              and associates — into one set of statements. KaiLux automatically detects intercompany
              eliminations using AI, saving hours of manual work.
            </div>
            <button onClick={startNewGroup}
              style={{ padding:'13px 32px', borderRadius:'8px', border:'none',
                background:T.prg, color:T.onpri,
                fontFamily:"'Outfit',sans-serif", fontSize:'14px',
                fontWeight:'700', cursor:'pointer',
                boxShadow:`0 6px 24px ${alpha(T.pri, 0.35)}` }}>
              Create First Group →
            </button>
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))', gap:'12px' }}>
            {groups.map(group => {
              const subCount  = (group.subsidiary_data || []).length
              const elimCount = (group.eliminations || []).length
              const generated = group.status === 'generated'
              return (
                <div key={group.id}
                  style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px',
                    padding:'20px', transition:'all 0.2s', cursor:'pointer' }}
                  onClick={() => editGroup(group.id)}
                  onMouseEnter={e => { e.currentTarget.style.borderColor=alpha(T.or, 0.31); e.currentTarget.style.transform='translateY(-2px)' }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor=T.bd; e.currentTarget.style.transform='translateY(0)' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'12px' }}>
                    <div style={{ width:'40px', height:'40px', borderRadius:'8px',
                      background:T.ord, display:'flex', alignItems:'center',
                      justifyContent:'center' }}><Construction size={20} strokeWidth={2} color={T.or}/></div>
                    <span style={{ fontSize:'9px', fontWeight:'700', padding:'3px 9px',
                      borderRadius:'6px',
                      background: generated ? T.gnd : T.yld,
                      color: generated ? T.gn : T.yl }}>
                      {generated ? 'GENERATED' : 'DRAFT'}
                    </span>
                  </div>
                  <div style={{ fontSize:'16px', fontWeight:'700', marginBottom:'4px' }}>
                    {group.group_name}
                  </div>
                  <div style={{ fontSize:'13px', color:T.mt, marginBottom:'10px' }}>
                    FY {group.financial_year} · {subCount} subsidiar{subCount===1?'y':'ies'} · {elimCount} elimination{elimCount!==1?'s':''}
                  </div>
                  {group.last_generated_at && (
                    <div style={{ fontSize:'12px', color:T.gn, marginBottom:'10px' }}>
                      <Icon name="check" /> Last generated {new Date(group.last_generated_at).toLocaleDateString('en-ZA',
                        { day:'numeric', month:'short', year:'numeric' })}
                    </div>
                  )}
                  <div style={{ padding:'8px 14px', borderRadius:'6px', border:'none',
                    background:T.prg, color:T.onpri,
                    fontFamily:"'Outfit',sans-serif", fontSize:'12px',
                    fontWeight:'700', textAlign:'center' }}>
                    {generated ? 'Open & Re-generate' : 'Continue →'}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )

  // ════════════════════════════════════════════════════════════════════════
  // RENDER — BUILDER
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'Outfit',sans-serif", color:T.tx }}>
      {/* Top bar */}
      <div style={{ background:T.s1, borderBottom:`1px solid ${T.bd}`, padding:'0 28px',
        height:'62px', display:'flex', alignItems:'center', justifyContent:'space-between',
        position:'sticky', top:0, zIndex:40 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
          <BackButton onClick={() => { setScreen('list'); loadGroups() }} label="Groups"
            textColor={T.mt} borderColor={T.bd} accentColor={T.or} />
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'17px', fontWeight:'800' }}>
            Kai<span style={{color:T.or}}>Lux</span>
          </div>
          {groupName && (
            <span style={{ fontSize:'13px', color:T.mt,
              borderLeft:`1px solid ${T.bd}`, paddingLeft:'12px' }}>
              {groupName}
            </span>
          )}
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          {builderStep !== 'done' && builderStep !== 'setup' && (
            <button onClick={handleStepBack}
              style={{ padding:'7px 14px', borderRadius:'6px',
                border:`1px solid ${T.bd}`, background:'transparent', color:T.mt,
                fontFamily:"'Outfit',sans-serif", fontSize:'13px', cursor:'pointer' }}>
              ← Back
            </button>
          )}
          {!['preview','done'].includes(builderStep) && (
            <button
              onClick={handleStepNext}
              disabled={builderStep==='setup'?!canProceedSetup:builderStep==='subsidiaries'?!canProceedSubs:false}
              style={{ padding:'7px 18px', borderRadius:'6px', border:'none',
                fontFamily:"'Outfit',sans-serif", fontSize:'13px', fontWeight:'700',
                cursor:'pointer', transition:'all 0.2s',
                background: (builderStep==='setup'&&!canProceedSetup)||(builderStep==='subsidiaries'&&!canProceedSubs)
                  ? T.s2 : T.prg,
                color: (builderStep==='setup'&&!canProceedSetup)||(builderStep==='subsidiaries'&&!canProceedSubs)
                  ? T.mt : T.onpri }}>
              {builderStep==='eliminations' ? 'Preview →' : 'Continue →'}
            </button>
          )}
        </div>
      </div>

      {/* Step indicator */}
      {builderStep !== 'done' && (
        <div style={{ background:T.s1, borderBottom:`1px solid ${T.bd}`,
          padding:'12px 28px', display:'flex', gap:'4px', alignItems:'center', flexWrap:'wrap' }}>
          {STEPS.map((step, i) => {
            const active  = step === builderStep
            const done    = STEPS.indexOf(builderStep) > i
            return (
              <div key={step} style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'4px',
                  padding:'5px 12px', borderRadius:'20px', cursor: done ? 'pointer' : 'default',
                  background: active ? T.ord : done ? T.gnd : 'transparent',
                  border:`1px solid ${active ? T.or : done ? T.gn : T.bd}` }}
                  onClick={() => done && setBuilderStep(step)}>
                  <div style={{ width:'18px', height:'18px', borderRadius:'50%', flexShrink:0,
                    background: active ? T.or : done ? T.gn : T.s2,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:'9px', fontWeight:'800', color:T.onpri }}>
                    {done ? <Icon name="check" /> : i + 1}
                  </div>
                  <span style={{ fontSize:'12px', fontWeight:'700',
                    color: active ? T.or : done ? T.gn : T.mt }}>
                    {STEP_LABELS[step]}
                  </span>
                </div>
                {i < STEPS.length - 1 && (
                  <div style={{ width:'20px', height:'1px', background: done ? T.gn : T.bd }}/>
                )}
              </div>
            )
          })}
        </div>
      )}

      <div style={{ padding:'28px 32px', maxWidth:'900px', margin:'0 auto' }}>

        {/* ── SETUP ─────────────────────────────────────────────────────── */}
        {builderStep === 'setup' && (
          <div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px',
              fontWeight:'800', marginBottom:'6px' }}>Group Setup</div>
            <div style={{ fontSize:'14px', color:T.mt, marginBottom:'24px' }}>
              Name the group, choose the financial year, and select the parent entity
            </div>

            <div style={{ background:T.s1, border:`1px solid ${T.bd}`,
              borderRadius:'12px', padding:'24px', marginBottom:'16px' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 160px', gap:'16px', marginBottom:'18px' }}>
                <div>
                  <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                    color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>GROUP NAME</label>
                  <input value={groupName} onChange={e => setGroupName(e.target.value)}
                    placeholder="e.g. Africakai Group"
                    style={{ width:'100%', padding:'11px 13px', background:T.s3,
                      border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
                      fontSize:'14px', fontFamily:"'Outfit',sans-serif",
                      outline:'none', boxSizing:'border-box' }}
                    onFocus={e => e.target.style.borderColor = T.or}
                    onBlur={e  => e.target.style.borderColor = T.bd}/>
                  <div style={{ fontSize:'10px', color:T.mt, marginTop:'4px' }}>
                    Appears as company name on the consolidated AFS cover page
                  </div>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                    color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>FINANCIAL YEAR</label>
                  <select value={financialYear} onChange={e => setFinancialYear(e.target.value)}
                    style={{ width:'100%', padding:'11px 13px', background:T.s3,
                      border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
                      fontSize:'14px', fontFamily:"'Outfit',sans-serif", outline:'none' }}>
                    {Array.from({ length: 11 }, (_, i) => i).map(offset => {
                      const yr = String(new Date().getFullYear() - offset)
                      return <option key={yr} value={yr} style={{ background:T.s2 }}>{yr}</option>
                    })}
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                  color:T.lt, letterSpacing:'1px', marginBottom:'8px' }}>
                  PARENT ENTITY
                </label>

                {parentClient ? (
                  <div style={{ display:'flex', alignItems:'center', gap:'8px',
                    padding:'12px 14px', background:T.gnd,
                    border:`1px solid ${alpha(T.gn, 0.25)}`, borderRadius:'8px', marginBottom:'8px' }}>
                    <div style={{ width:'36px', height:'36px', borderRadius:'8px',
                      background:T.prg,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:'14px', fontWeight:'800', color:T.onpri, flexShrink:0 }}>
                      {parentClient.name[0]}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:'14px', fontWeight:'700', color:T.gn }}>
                        {parentClient.name}
                      </div>
                      <div style={{ fontSize:'12px', color:T.mt }}>
                        {parentClient.entity_type} · Parent entity (100%)
                      </div>
                    </div>
                    <button onClick={() => { setParentClient(null); setClientSearch('') }}
                      style={{ background:'none', border:'none', color:T.mt,
                        cursor:'pointer', fontSize:'16px' }}>×</button>
                  </div>
                ) : (
                  <>
                    <input value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                      placeholder="Search for the holding / parent company..."
                      style={{ width:'100%', padding:'10px 13px', background:T.s3,
                        border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
                        fontSize:'14px', fontFamily:"'Outfit',sans-serif",
                        outline:'none', boxSizing:'border-box', marginBottom:'6px' }}
                      onFocus={e => e.target.style.borderColor = T.or}
                      onBlur={e  => e.target.style.borderColor = T.bd}/>
                    {filteredParentClients.slice(0, 8).map(client => (
                      <div key={client.id}
                        onClick={() => { setParentClient(client); setClientSearch('') }}
                        style={{ display:'flex', gap:'8px', alignItems:'center',
                          padding:'10px 14px', borderBottom:`1px solid ${T.bd}`,
                          cursor:'pointer', transition:'background 0.15s',
                          background:T.s2, borderRadius: filteredParentClients.indexOf(client)===0?'8px 8px 0 0':filteredParentClients.indexOf(client)===Math.min(filteredParentClients.length,8)-1?'0 0 8px 8px':'0' }}
                        onMouseEnter={e => e.currentTarget.style.background = T.s3}
                        onMouseLeave={e => e.currentTarget.style.background = T.s2}>
                        <div style={{ width:'30px', height:'30px', borderRadius:'6px',
                          background:T.ord, display:'flex', alignItems:'center',
                          justifyContent:'center', fontSize:'13px', fontWeight:'800',
                          color:T.or, flexShrink:0 }}>{client.name[0]}</div>
                        <div>
                          <div style={{ fontSize:'13px', fontWeight:'600' }}>{client.name}</div>
                          <div style={{ fontSize:'10px', color:T.mt }}>{client.entity_type} · {client.industry}</div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>

            {canProceedSetup && (
              <button onClick={handleStepNext}
                style={{ padding:'12px 28px', borderRadius:'8px', border:'none',
                  background:T.prg, color:T.onpri,
                  fontFamily:"'Outfit',sans-serif", fontSize:'14px',
                  fontWeight:'700', cursor:'pointer' }}>
                Add Subsidiaries →
              </button>
            )}
          </div>
        )}

        {/* ── SUBSIDIARIES ──────────────────────────────────────────────── */}
        {builderStep === 'subsidiaries' && (
          <div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px',
              fontWeight:'800', marginBottom:'6px' }}>Group Structure</div>
            <div style={{ fontSize:'14px', color:T.mt, marginBottom:'24px' }}>
              Add subsidiaries and set the parent's ownership percentage in each entity.
              Minority interest is calculated automatically for partial ownership.
            </div>

            {/* Entity tree */}
            <div style={{ background:T.s1, border:`1px solid ${T.bd}`,
              borderRadius:'12px', padding:'20px', marginBottom:'20px' }}>
              <div style={{ fontSize:'10px', color:T.lt, fontWeight:'700',
                letterSpacing:'1px', marginBottom:'14px' }}>GROUP STRUCTURE PREVIEW</div>

              {/* Parent node */}
              <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'10px' }}>
                <div style={{ width:'40px', height:'40px', borderRadius:'8px',
                  background:T.prg,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:'16px', fontWeight:'800', color:T.onpri, flexShrink:0 }}>
                  {parentClient?.name?.[0]}
                </div>
                <div>
                  <div style={{ fontSize:'14px', fontWeight:'700' }}>{parentClient?.name}</div>
                  <div style={{ fontSize:'10px', color:T.mt }}>
                    Parent entity · 100% ownership
                    {entityFigures[parentClient?.id] && Object.keys(entityFigures[parentClient.id]).length > 0
                      ? ` · Revenue ${fmtR(parseFloat(entityFigures[parentClient.id]?.['Revenue']||0))}`
                      : ''}
                  </div>
                </div>
              </div>

              {/* Subsidiary nodes */}
              {subsidiaries.map(sub => (
                <div key={sub.client.id} style={{ display:'flex', alignItems:'flex-start',
                  gap:'0', paddingLeft:'20px', marginTop:'8px' }}>
                  <div style={{ display:'flex', flexDirection:'column', width:'20px',
                    alignItems:'center', marginRight:'8px', alignSelf:'stretch', paddingTop:'4px' }}>
                    <div style={{ width:'1px', background:T.bd, flex:1, minHeight:'16px' }}/>
                    <div style={{ width:'14px', height:'1px', background:T.bd, alignSelf:'flex-end' }}/>
                    <div style={{ flex:1 }}/>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:'6px', flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px',
                    background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'8px',
                    padding:'10px 14px', flex:1 }}>
                    <div style={{ width:'32px', height:'32px', borderRadius:'8px',
                      background:T.bld, border:`1px solid ${alpha(T.bl, 0.19)}`,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:'13px', fontWeight:'800', color:T.bl, flexShrink:0 }}>
                      {sub.client.name?.[0]}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:'13px', fontWeight:'600',
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {sub.client.name}
                      </div>
                      <div style={{ fontSize:'10px', color:T.mt }}>{sub.client.entity_type}</div>
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', flexShrink:0 }}>
                      <span style={{ fontSize:'10px', color:T.mt }}>Ownership</span>
                      <input type="number" min="1" max="100" value={sub.ownership_pct}
                        onChange={e => updateOwnership(sub.client.id, e.target.value)}
                        style={{ width:'56px', padding:'5px 7px', background:T.s3,
                          border:`1px solid ${T.bd}`, borderRadius:'6px', color:T.tx,
                          fontSize:'13px', fontFamily:"'DM Mono',monospace",
                          outline:'none', textAlign:'right' }}
                        onFocus={e => e.target.style.borderColor = T.or}
                        onBlur={e  => e.target.style.borderColor = T.bd}/>
                      <span style={{ fontSize:'12px', color:T.mt }}>%</span>
                      {sub.ownership_pct < 100 && (
                        <span style={{ fontSize:'9px', fontWeight:'700', padding:'2px 6px',
                          borderRadius:'6px', background:T.yld, color:T.yl }}>
                          MI {100 - sub.ownership_pct}%
                        </span>
                      )}
                    </div>
                    <button onClick={() => removeSubsidiary(sub.client.id)}
                      style={{ background:'none', border:'none', color:T.mt,
                        cursor:'pointer', fontSize:'16px', padding:'0 2px' }}>×</button>
                  </div>

                  {/* Phase 2b (10 Sep 2026) — goodwill/acquisition accounting.
                      Collapsed by default since the overwhelming majority of
                      subsidiaries are formed, not acquired, and have no
                      goodwill at all — a subsidiary with this left unchecked
                      behaves exactly as Phase 2a already does. */}
                  <label style={{ display:'flex', alignItems:'center', gap:'6px',
                    fontSize:'11px', color:T.mt, cursor:'pointer', paddingLeft:'2px' }}>
                    <input type="checkbox" checked={!!sub.isAcquisition}
                      onChange={e => updateSubsidiaryField(sub.client.id, 'isAcquisition', e.target.checked)}/>
                    This subsidiary was acquired (not formed) — add goodwill accounting details
                  </label>
                  {sub.isAcquisition && (
                    <div style={{ background:T.s3, border:`1px solid ${T.bd}`, borderRadius:'8px',
                      padding:'12px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                      <div>
                        <label style={{ fontSize:'10px', color:T.mt, display:'block', marginBottom:'4px' }}>
                          Acquisition date
                        </label>
                        <input type="date" value={sub.acquisition_date || ''}
                          onChange={e => updateSubsidiaryField(sub.client.id, 'acquisition_date', e.target.value)}
                          style={{ width:'100%', padding:'6px 8px', background:T.s2,
                            border:`1px solid ${T.bd}`, borderRadius:'6px', color:T.tx, fontSize:'12px' }}/>
                      </div>
                      <div>
                        <label style={{ fontSize:'10px', color:T.mt, display:'block', marginBottom:'4px' }}>
                          Consideration paid (R)
                        </label>
                        <input type="number" min="0" value={sub.consideration_paid || ''}
                          placeholder="e.g. 500000"
                          onChange={e => updateSubsidiaryField(sub.client.id, 'consideration_paid', e.target.value)}
                          style={{ width:'100%', padding:'6px 8px', background:T.s2,
                            border:`1px solid ${T.bd}`, borderRadius:'6px', color:T.tx, fontSize:'12px' }}/>
                      </div>
                      <div>
                        <label style={{ fontSize:'10px', color:T.mt, display:'block', marginBottom:'4px' }}>
                          Fair value of net assets acquired (R)
                        </label>
                        <input type="number" min="0" value={sub.fair_value_net_assets || ''}
                          placeholder="Leave blank to use book value"
                          onChange={e => updateSubsidiaryField(sub.client.id, 'fair_value_net_assets', e.target.value)}
                          style={{ width:'100%', padding:'6px 8px', background:T.s2,
                            border:`1px solid ${T.bd}`, borderRadius:'6px', color:T.tx, fontSize:'12px' }}/>
                        <div style={{ fontSize:'9px', color:T.mt, marginTop:'3px' }}>
                          No formal valuation? Leave blank — the subsidiary's own reported net assets at
                          acquisition will be used as a reasonable proxy, and this will be disclosed in the notes.
                        </div>
                      </div>
                      <div>
                        <label style={{ fontSize:'10px', color:T.mt, display:'block', marginBottom:'4px' }}>
                          Fair value adjustment (R, optional)
                        </label>
                        <input type="number" value={sub.fair_value_adjustment || ''}
                          placeholder="e.g. property revalued — 0 if none"
                          onChange={e => updateSubsidiaryField(sub.client.id, 'fair_value_adjustment', e.target.value)}
                          style={{ width:'100%', padding:'6px 8px', background:T.s2,
                            border:`1px solid ${T.bd}`, borderRadius:'6px', color:T.tx, fontSize:'12px' }}/>
                      </div>
                      <div>
                        <label style={{ fontSize:'10px', color:T.mt, display:'block', marginBottom:'4px' }}>
                          Goodwill useful life (years)
                        </label>
                        <input type="number" min="1" value={sub.useful_life_years || ''}
                          placeholder="10 (IFRS for SMEs default)"
                          onChange={e => updateSubsidiaryField(sub.client.id, 'useful_life_years', e.target.value)}
                          style={{ width:'100%', padding:'6px 8px', background:T.s2,
                            border:`1px solid ${T.bd}`, borderRadius:'6px', color:T.tx, fontSize:'12px' }}/>
                      </div>
                    </div>
                  )}
                  </div>
                </div>
              ))}

              {subsidiaries.length === 0 && (
                <div style={{ paddingLeft:'28px', marginTop:'6px',
                  fontSize:'13px', color:T.mt, fontStyle:'italic' }}>
                  No subsidiaries added — search below to add
                </div>
              )}
            </div>

            {/* Add subsidiary */}
            <div style={{ background:T.s1, border:`1px solid ${T.bd}`,
              borderRadius:'12px', padding:'20px', marginBottom:'16px' }}>
              <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                color:T.lt, letterSpacing:'1px', marginBottom:'8px' }}>
                ADD SUBSIDIARY ENTITY
              </label>
              <input value={subSearch} onChange={e => setSubSearch(e.target.value)}
                placeholder="Search for a client to add as subsidiary..."
                style={{ width:'100%', padding:'10px 13px', background:T.s3,
                  border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
                  fontSize:'14px', fontFamily:"'Outfit',sans-serif",
                  outline:'none', boxSizing:'border-box' }}
                onFocus={e => e.target.style.borderColor = T.or}
                onBlur={e  => e.target.style.borderColor = T.bd}/>

              {subSearch && filteredSubClients.length > 0 && (
                <div style={{ background:T.s2, border:`1px solid ${T.bd}`,
                  borderRadius:'8px', marginTop:'8px', maxHeight:'200px', overflowY:'auto' }}>
                  {filteredSubClients.slice(0, 8).map(client => (
                    <div key={client.id} onClick={() => addSubsidiary(client)}
                      style={{ display:'flex', gap:'8px', alignItems:'center',
                        padding:'10px 14px', borderBottom:`1px solid ${T.bd}`,
                        cursor:'pointer' }}
                      onMouseEnter={e => e.currentTarget.style.background = T.s3}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div style={{ width:'28px', height:'28px', borderRadius:'6px',
                        background:T.bld, display:'flex', alignItems:'center',
                        justifyContent:'center', fontSize:'13px', fontWeight:'800',
                        color:T.bl, flexShrink:0 }}>{client.name[0]}</div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:'13px', fontWeight:'600' }}>{client.name}</div>
                        <div style={{ fontSize:'10px', color:T.mt }}>{client.entity_type}</div>
                      </div>
                      <span style={{ fontSize:'12px', color:T.or, fontWeight:'700' }}>+ Add</span>
                    </div>
                  ))}
                </div>
              )}
              {subSearch && filteredSubClients.length === 0 && (
                <div style={{ marginTop:'8px', padding:'12px', background:T.s2, borderRadius:'8px',
                  fontSize:'13px', color:T.mt, textAlign:'center' }}>
                  No matching clients. All subsidiaries must first exist as clients.
                </div>
              )}
              <div style={{ fontSize:'12px', color:T.mt, marginTop:'8px' }}>
                All entities must already exist as clients in KaiLux and have at least one completed AFS.
              </div>
            </div>

            {canProceedSubs && (
              <button onClick={handleStepNext}
                style={{ padding:'12px 28px', borderRadius:'8px', border:'none',
                  background:T.prg, color:T.onpri,
                  fontFamily:"'Outfit',sans-serif", fontSize:'14px',
                  fontWeight:'700', cursor:'pointer' }}>
                Set Eliminations →
              </button>
            )}
          </div>
        )}

        {/* ── ELIMINATIONS ──────────────────────────────────────────────── */}
        {builderStep === 'eliminations' && (
          <div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px',
              fontWeight:'800', marginBottom:'6px' }}>Intercompany Eliminations</div>
            <div style={{ fontSize:'14px', color:T.mt, marginBottom:'24px', lineHeight:'1.7' }}>
              Eliminations remove transactions between group entities from the consolidated statements.
              Use AI to auto-detect probable items, or add them manually.
            </div>

            {/* Per-year elimination history (feat/consolidation-per-year-eliminations,
                feat/consolidation-prior-year-elimination-editing) — shows which financial
                years have a saved elimination snapshot, so a preparer knows which prior
                -year comparatives will be genuinely re-eliminated on generate versus shown
                as an unadjusted aggregate, AND lets them fix a PRIOR year's tracked
                eliminations directly (via PUT .../elimination-history/{year}) without
                disturbing the group's own current financial_year/eliminations. */}
            <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'10px',
              padding:'14px 16px', marginBottom:'16px' }}>
              <div style={{ fontSize:'12.5px', color:T.mt, lineHeight:'1.6', marginBottom: eliminationHistoryYears.length ? '10px' : 0 }}>
                {eliminationHistoryYears.length > 0 ? (
                  <>
                    <span style={{ color:T.tx, fontWeight:'700' }}>Eliminations tracked for: </span>
                    {eliminationHistoryYears.join(', ')}
                    {!eliminationHistoryYears.includes(financialYear) && (
                      <> — saving eliminations for FY {financialYear} will add it to this list.</>
                    )}
                    {' '}Only tracked years are re-eliminated when they appear as a prior-year comparative.
                  </>
                ) : (
                  <>No elimination history tracked yet for this group. Saving eliminations here will
                    be tracked for FY {financialYear} — untracked prior years show an unadjusted
                    (aggregated, not re-eliminated) comparative.</>
                )}
              </div>

              {/* Prior years only — the CURRENT financialYear is edited via the normal
                  confirmed-eliminations list below, not here. */}
              {eliminationHistory.filter(h => h.financial_year !== financialYear).map(h => (
                <div key={h.financial_year} style={{ marginTop:'8px' }}>
                  {editingHistoryYear === h.financial_year ? (
                    <div style={{ background:T.s2, border:`1px solid ${alpha(T.or, 0.25)}`,
                      borderRadius:'8px', padding:'14px' }}>
                      <div style={{ fontSize:'12px', color:T.or, fontWeight:'700',
                        letterSpacing:'0.5px', marginBottom:'10px' }}>
                        EDITING FY {h.financial_year} ELIMINATIONS
                      </div>
                      {historyDraft.length === 0 && (
                        <div style={{ fontSize:'12px', color:T.mt, fontStyle:'italic', marginBottom:'8px' }}>
                          No eliminations for this year yet — add one below.
                        </div>
                      )}
                      {historyDraft.map(e => (
                        <div key={e._k} style={{ display:'flex', alignItems:'center', gap:'8px',
                          background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'6px',
                          padding:'8px 10px', marginBottom:'6px' }}>
                          <div style={{ flex:1, fontSize:'12px' }}>
                            DR {e.dr_category} / CR {e.cr_category}
                            <span style={{ marginLeft:'8px', color:T.or, fontWeight:'700',
                              fontFamily:"'DM Mono',monospace" }}>
                              R{(parseFloat(e.amount)||0).toLocaleString('en-ZA',{maximumFractionDigits:0})}
                            </span>
                          </div>
                          <button onClick={() => removeHistoryDraftElim(e._k)}
                            style={{ background:'none', border:'none', color:T.mt,
                              cursor:'pointer', fontSize:'15px', padding:'0 2px' }}>×</button>
                        </div>
                      ))}
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 110px', gap:'6px', margin:'10px 0' }}>
                        <select value={historyNewElim.dr_category}
                          onChange={e => setHistoryNewElim(p => ({ ...p, dr_category: e.target.value }))}
                          style={{ padding:'7px 9px', background:T.s3, border:`1px solid ${T.bd}`,
                            borderRadius:'6px', color:T.tx, fontSize:'11px', outline:'none' }}>
                          {IFRS_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <select value={historyNewElim.cr_category}
                          onChange={e => setHistoryNewElim(p => ({ ...p, cr_category: e.target.value }))}
                          style={{ padding:'7px 9px', background:T.s3, border:`1px solid ${T.bd}`,
                            borderRadius:'6px', color:T.tx, fontSize:'11px', outline:'none' }}>
                          {IFRS_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <input type="number" value={historyNewElim.amount}
                          placeholder="Amount"
                          onChange={e => setHistoryNewElim(p => ({ ...p, amount: e.target.value }))}
                          style={{ padding:'7px 9px', background:T.s3, border:`1px solid ${T.bd}`,
                            borderRadius:'6px', color:T.tx, fontSize:'12px',
                            fontFamily:"'DM Mono',monospace", textAlign:'right', outline:'none' }}/>
                      </div>
                      <div style={{ display:'flex', gap:'8px' }}>
                        <button onClick={addHistoryDraftElim} disabled={!historyNewElim.amount}
                          style={{ padding:'7px 14px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                            background:'transparent', color:T.tx, fontSize:'12px', cursor:'pointer' }}>
                          + Add row
                        </button>
                        <div style={{ flex:1 }}/>
                        <button onClick={() => setEditingHistoryYear(null)} disabled={historySaving}
                          style={{ padding:'7px 14px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                            background:'transparent', color:T.mt, fontSize:'12px', cursor:'pointer' }}>
                          Cancel
                        </button>
                        <button onClick={() => saveHistoryYear(h.financial_year)} disabled={historySaving}
                          style={{ padding:'7px 16px', borderRadius:'6px', border:'none',
                            background:T.prg, color:T.onpri,
                            fontSize:'12px', fontWeight:'700', cursor:'pointer',
                            opacity: historySaving ? 0.6 : 1 }}>
                          {historySaving ? 'Saving...' : `Save FY ${h.financial_year}`}
                        </button>
                      </div>
                      {historySaveError && (
                        <div style={{ marginTop:'8px', fontSize:'12px', color:T.yl }}><Icon name="alert" /> {historySaveError}</div>
                      )}
                    </div>
                  ) : (
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                      background:T.s2, border:`1px solid ${T.bd}`, borderRadius:'6px', padding:'8px 12px' }}>
                      <span style={{ fontSize:'12px', color:T.tx }}>
                        FY {h.financial_year} — {(h.eliminations || []).length} elimination{(h.eliminations || []).length !== 1 ? 's' : ''} tracked
                      </span>
                      <button onClick={() => startEditingHistoryYear(h.financial_year)}
                        style={{ padding:'5px 12px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                          background:'transparent', color:T.or, fontSize:'12px', fontWeight:'700',
                          cursor:'pointer' }}>
                        Edit
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* AI detection */}
            <div style={{ background:T.s1, border:`1px solid ${T.bd}`,
              borderRadius:'12px', padding:'18px 20px', marginBottom:'16px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:'12px' }}>
                <div>
                  <div style={{ fontSize:'14px', fontWeight:'700', marginBottom:'3px' }}>
                    <Icon name="ai" /> AI Intercompany Detection
                  </div>
                  <div style={{ fontSize:'13px', color:T.mt, lineHeight:'1.5' }}>
                    KaiLux analyses all entity figures and flags probable intercompany balances — loans,
                    management fees, trade balances, and investment eliminations
                  </div>
                </div>
                <button onClick={handleDetect} disabled={detecting}
                  style={{ padding:'9px 18px', borderRadius:'8px', border:'none', flexShrink:0,
                    background: detecting ? T.s2 : T.bl,
                    color: detecting ? T.mt : T.onpri, fontFamily:"'Outfit',sans-serif",
                    fontSize:'13px', fontWeight:'700',
                    cursor: detecting ? 'not-allowed' : 'pointer',
                    display:'flex', alignItems:'center', gap:'8px', opacity: detecting ? 0.7 : 1 }}>
                  {detecting
                    ? <><span style={{ width:'12px', height:'12px', border:`2px solid ${alpha(T.onpri, 0.3)}`,
                        borderTopColor:T.onpri, borderRadius:'50%', animation:'spin 0.8s linear infinite',
                        display:'inline-block' }}/>Detecting...</>
                    : <><Icon name="search" /> Auto-detect</>}
                </button>
              </div>
              {detectError && (
                <div style={{ marginTop:'10px', fontSize:'13px', color:T.yl }}><Icon name="alert" /> {detectError}</div>
              )}
            </div>

            {/* AI suggestions */}
            {suggestions.length > 0 && (
              <div style={{ marginBottom:'16px' }}>
                <div style={{ fontSize:'12px', color:T.bl, fontWeight:'700',
                  letterSpacing:'1px', marginBottom:'8px' }}>
                  <Icon name="ai" /> AI SUGGESTIONS — {suggestions.length} FOUND
                </div>
                {suggestions.map(sug => (
                  <div key={sug.id}
                    style={{ background:T.bld, border:`1px solid ${alpha(T.bl, 0.2)}`,
                      borderRadius:'8px', padding:'14px 16px', marginBottom:'6px',
                      display:'flex', alignItems:'flex-start', gap:'12px' }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:'13px', fontWeight:'700', marginBottom:'3px' }}>
                        {sug.description}
                      </div>
                      <div style={{ fontSize:'12px', color:T.mt, marginBottom:'3px' }}>
                        DR {sug.dr_category} / CR {sug.cr_category} ·{' '}
                        <span style={{ color:T.or, fontWeight:'700', fontFamily:"'DM Mono',monospace" }}>
                          R{(parseFloat(sug.amount)||0).toLocaleString('en-ZA',{maximumFractionDigits:0})}
                        </span>
                      </div>
                      {sug.reasoning && (
                        <div style={{ fontSize:'10px', color:T.bl }}>{sug.reasoning}</div>
                      )}
                      {sug.confidence && (
                        <div style={{ marginTop:'4px', fontSize:'9px', color:T.mt }}>
                          Confidence: {typeof sug.confidence === 'number' ? `${sug.confidence}%` : sug.confidence}
                        </div>
                      )}
                    </div>
                    <div style={{ display:'flex', gap:'4px', flexShrink:0 }}>
                      <button onClick={() => acceptSuggestion(sug)}
                        style={{ padding:'6px 14px', borderRadius:'6px', border:'none',
                          background:T.gnd, color:T.gn, fontFamily:"'Outfit',sans-serif",
                          fontSize:'12px', fontWeight:'700', cursor:'pointer' }}>
                        <Icon name="check" /> Accept
                      </button>
                      <button onClick={() => setSuggestions(prev => prev.filter(s => s.id !== sug.id))}
                        style={{ padding:'6px 12px', borderRadius:'6px',
                          border:`1px solid ${T.bd}`, background:'transparent', color:T.mt,
                          fontFamily:"'Outfit',sans-serif", fontSize:'12px', cursor:'pointer' }}>
                        Ignore
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Confirmed eliminations */}
            {eliminations.length > 0 && (
              <div style={{ marginBottom:'16px' }}>
                <div style={{ fontSize:'12px', color:T.gn, fontWeight:'700',
                  letterSpacing:'1px', marginBottom:'8px' }}>
                  <Icon name="check" /> CONFIRMED ELIMINATIONS — {eliminations.length}
                </div>
                {eliminations.map(elim => (
                  <div key={elim.id}
                    style={{ background:T.s1, border:`1px solid ${T.bd}`,
                      borderRadius:'8px', padding:'11px 14px', marginBottom:'6px',
                      display:'flex', alignItems:'center', gap:'8px' }}>
                    <div style={{ width:'7px', height:'7px', borderRadius:'50%', flexShrink:0,
                      background: elim.manual ? T.yl : T.bl }}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:'13px', fontWeight:'700' }}>{elim.description}</div>
                      <div style={{ fontSize:'12px', color:T.mt }}>
                        DR {elim.dr_category} / CR {elim.cr_category}
                        <span style={{ marginLeft:'8px', color:T.or, fontWeight:'700',
                          fontFamily:"'DM Mono',monospace" }}>
                          R{(parseFloat(elim.amount)||0).toLocaleString('en-ZA',{maximumFractionDigits:0})}
                        </span>
                        <span style={{ marginLeft:'8px', fontSize:'9px', padding:'1px 5px',
                          borderRadius:'6px',
                          background: elim.manual ? T.yld : T.bld,
                          color: elim.manual ? T.yl : T.bl }}>
                          {elim.manual ? 'MANUAL' : 'AI'}
                        </span>
                      </div>
                    </div>
                    <button onClick={() => removeElimination(elim.id)}
                      style={{ background:'none', border:'none', color:T.mt,
                        cursor:'pointer', fontSize:'15px', padding:'0 2px' }}>×</button>
                  </div>
                ))}
                <div style={{ fontSize:'12px', color:T.mt, marginTop:'6px',
                  padding:'8px 12px', background:T.s2, borderRadius:'6px' }}>
                  Total eliminated per side: R{eliminations.reduce((s,e)=>s+(parseFloat(e.amount)||0),0)
                    .toLocaleString('en-ZA',{maximumFractionDigits:0})}
                </div>
              </div>
            )}

            {/* Add manual elimination */}
            {addingElim ? (
              <div style={{ background:T.s1, border:`1px solid ${alpha(T.or, 0.25)}`,
                borderRadius:'12px', padding:'20px', marginBottom:'16px' }}>
                <div style={{ fontSize:'12px', color:T.or, fontWeight:'700',
                  letterSpacing:'1px', marginBottom:'14px' }}>ADD MANUAL ELIMINATION</div>

                <div style={{ marginBottom:'12px' }}>
                  <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                    color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>TYPE</label>
                  <select value={newElim.type_id}
                    onChange={e => {
                      const t = ELIM_TYPES.find(x => x.id === e.target.value)
                      setNewElim(p => ({ ...p, type_id: e.target.value, dr_category: t?.dr || p.dr_category, cr_category: t?.cr || p.cr_category }))
                    }}
                    style={{ width:'100%', padding:'9px 12px', background:T.s3,
                      border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
                      fontSize:'14px', fontFamily:"'Outfit',sans-serif", outline:'none' }}>
                    {ELIM_TYPES.map(t => (
                      <option key={t.id} value={t.id} style={{ background:T.s2 }}>
                        {t.label} — {t.desc}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom:'12px' }}>
                  <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                    color:T.lt, letterSpacing:'1px', marginBottom:'6px' }}>DESCRIPTION</label>
                  <input value={newElim.description}
                    onChange={e => setNewElim(p => ({...p, description: e.target.value}))}
                    placeholder="e.g. Loan from parent to Subsidiary A"
                    style={{ width:'100%', padding:'9px 12px', background:T.s3,
                      border:`1px solid ${T.bd}`, borderRadius:'8px', color:T.tx,
                      fontSize:'14px', fontFamily:"'Outfit',sans-serif",
                      outline:'none', boxSizing:'border-box' }}
                    onFocus={e => e.target.style.borderColor = T.or}
                    onBlur={e  => e.target.style.borderColor = T.bd}/>
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 130px', gap:'8px', marginBottom:'14px' }}>
                  <div>
                    <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                      color:T.lt, letterSpacing:'1px', marginBottom:'5px' }}>DR — REDUCE</label>
                    <select value={newElim.dr_category}
                      onChange={e => setNewElim(p => ({...p, dr_category: e.target.value}))}
                      style={{ width:'100%', padding:'8px 10px', background:T.s3,
                        border:`1px solid ${T.bd}`, borderRadius:'6px', color:T.tx,
                        fontSize:'12px', fontFamily:"'Outfit',sans-serif", outline:'none' }}>
                      {IFRS_CATS.map(c => <option key={c} value={c} style={{ background:T.s2 }}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                      color:T.lt, letterSpacing:'1px', marginBottom:'5px' }}>CR — REDUCE</label>
                    <select value={newElim.cr_category}
                      onChange={e => setNewElim(p => ({...p, cr_category: e.target.value}))}
                      style={{ width:'100%', padding:'8px 10px', background:T.s3,
                        border:`1px solid ${T.bd}`, borderRadius:'6px', color:T.tx,
                        fontSize:'12px', fontFamily:"'Outfit',sans-serif", outline:'none' }}>
                      {IFRS_CATS.map(c => <option key={c} value={c} style={{ background:T.s2 }}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:'10px', fontWeight:'700',
                      color:T.lt, letterSpacing:'1px', marginBottom:'5px' }}>AMOUNT (R)</label>
                    <input type="number" value={newElim.amount}
                      onChange={e => setNewElim(p => ({...p, amount: e.target.value}))}
                      placeholder="0"
                      style={{ width:'100%', padding:'8px 10px', background:T.s3,
                        border:`1px solid ${T.bd}`, borderRadius:'6px', color:T.tx,
                        fontSize:'13px', fontFamily:"'DM Mono',monospace",
                        outline:'none', boxSizing:'border-box', textAlign:'right' }}
                      onFocus={e => e.target.style.borderColor = T.or}
                      onBlur={e  => e.target.style.borderColor = T.bd}/>
                  </div>
                </div>

                <div style={{ display:'flex', gap:'8px' }}>
                  <button onClick={addElimination}
                    disabled={!newElim.amount || !newElim.dr_category}
                    style={{ padding:'9px 20px', borderRadius:'8px', border:'none',
                      background:T.prg, color:T.onpri,
                      fontFamily:"'Outfit',sans-serif", fontSize:'13px',
                      fontWeight:'700', cursor:'pointer' }}>
                    Add Elimination
                  </button>
                  <button onClick={() => setAddingElim(false)}
                    style={{ padding:'9px 14px', borderRadius:'8px',
                      border:`1px solid ${T.bd}`, background:'transparent', color:T.mt,
                      fontFamily:"'Outfit',sans-serif", fontSize:'13px', cursor:'pointer' }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAddingElim(true)}
                style={{ width:'100%', padding:'12px', borderRadius:'8px',
                  border:`1px dashed ${T.bd}`, background:'transparent', color:T.mt,
                  fontFamily:"'Outfit',sans-serif", fontSize:'14px', cursor:'pointer',
                  marginBottom:'16px', transition:'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor=T.or; e.currentTarget.style.color=T.or }}
                onMouseLeave={e => { e.currentTarget.style.borderColor=T.bd; e.currentTarget.style.color=T.mt }}>
                + Add Manual Elimination
              </button>
            )}

            <div style={{ display:'flex', gap:'8px' }}>
              <button onClick={handleStepBack}
                style={{ padding:'11px 20px', borderRadius:'8px',
                  border:`1px solid ${T.bd}`, background:'transparent', color:T.mt,
                  fontFamily:"'Outfit',sans-serif", fontSize:'14px', cursor:'pointer' }}>
                ← Back
              </button>
              <button onClick={() => setBuilderStep('preview')}
                style={{ padding:'11px 28px', borderRadius:'8px', border:'none',
                  background:T.prg, color:T.onpri,
                  fontFamily:"'Outfit',sans-serif", fontSize:'14px',
                  fontWeight:'700', cursor:'pointer' }}>
                Preview & Generate →
              </button>
            </div>
          </div>
        )}

        {/* ── PREVIEW ───────────────────────────────────────────────────── */}
        {builderStep === 'preview' && (
          <div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px',
              fontWeight:'800', marginBottom:'6px' }}>
              Consolidated Preview — {groupName}
            </div>
            <div style={{ fontSize:'14px', color:T.mt, marginBottom:'20px' }}>
              FY {financialYear} · {subsidiaries.length + 1} entities ·{' '}
              {eliminations.length} elimination{eliminations.length !== 1 ? 's' : ''}
              {minorityInterest > 0 && ` · Minority interest ${fmtR(minorityInterest)}`}
            </div>

            {/* KPI cards */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'8px', marginBottom:'20px' }}>
              {[
                { label:'Revenue',         value: parseFloat(consolidatedFigs['Revenue']||0),      color:T.tx  },
                { label:'Profit After Tax', value: consolidatedDerived['_pat']||0,                 color: (consolidatedDerived['_pat']||0) >= 0 ? T.gn : T.rd },
                { label:'Total Assets',    value: consolidatedDerived['_totalAssets']||0,          color:T.tx  },
                { label:'Total Equity',    value: consolidatedDerived['_totalEquity']||0,          color: (consolidatedDerived['_totalEquity']||0) >= 0 ? T.gn : T.rd },
              ].map((kpi,i) => (
                <div key={i} style={{ background:T.s1, border:`1px solid ${T.bd}`,
                  borderRadius:'12px', padding:'16px' }}>
                  <div style={{ fontSize:'9px', color:T.mt, letterSpacing:'1px',
                    marginBottom:'8px' }}>{kpi.label.toUpperCase()}</div>
                  <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'20px',
                    fontWeight:'800', color:kpi.color, letterSpacing:'-0.5px' }}>
                    {fmtR(kpi.value)}
                  </div>
                  <div style={{ fontSize:'9px', color:T.mt, marginTop:'3px' }}>Consolidated</div>
                </div>
              ))}
            </div>

            {/* Comparison table */}
            <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px',
              overflow:'hidden', marginBottom:'20px' }}>
              <div style={{ padding:'12px 16px', background:T.s2, borderBottom:`1px solid ${T.bd}`,
                fontSize:'10px', color:T.lt, fontWeight:'700', letterSpacing:'1.5px' }}>
                ENTITY-BY-ENTITY COMPARISON
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse',
                  minWidth: `${500 + subsidiaries.length * 110}px` }}>
                  <thead>
                    <tr style={{ background:T.s2, borderBottom:`2px solid ${T.bd}` }}>
                      <th style={{ textAlign:'left', padding:'10px 14px', fontSize:'10px',
                        color:T.mt, fontWeight:'700', minWidth:'160px' }}>Figure</th>
                      <th style={{ textAlign:'right', padding:'10px 12px',
                        fontSize:'10px', color:T.or, fontWeight:'700', whiteSpace:'nowrap' }}>
                        {parentClient?.name?.split(' ').slice(0,2).join(' ')} (Parent)
                      </th>
                      {subsidiaries.map(sub => (
                        <th key={sub.client.id} style={{ textAlign:'right', padding:'10px 12px',
                          fontSize:'10px', color:T.bl, fontWeight:'700', whiteSpace:'nowrap' }}>
                          {sub.client.name?.split(' ')[0]} ({sub.ownership_pct}%)
                        </th>
                      ))}
                      {eliminations.length > 0 && (
                        <th style={{ textAlign:'right', padding:'10px 12px',
                          fontSize:'10px', color:T.rd, fontWeight:'700' }}>Eliminations</th>
                      )}
                      <th style={{ textAlign:'right', padding:'10px 12px',
                        fontSize:'10px', color:T.gn, fontWeight:'700' }}>Consolidated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...KEY_ROWS_INCOME,
                      { key:'_sep', sep:true },
                      ...KEY_ROWS_BALANCE
                    ].map((row, ri) => {
                      if (row.sep) return (
                        <tr key={ri} style={{ borderBottom:`2px solid ${T.bd}` }}>
                          <td colSpan={3 + subsidiaries.length + (eliminations.length > 0 ? 1 : 0)}
                            style={{ padding:'3px 0' }}/>
                        </tr>
                      )

                      const parentDerived = derivedFigures(entityFigures[parentClient?.id] || {})
                      const consVal = consolidatedDerived[row.key]
                      const hasConsVal = consVal !== null && consVal !== undefined && consVal !== 0

                      // Sum of eliminations affecting this row
                      const elimEffect = eliminations.reduce((sum, e) => {
                        if (e.dr_category === row.key || e.cr_category === row.key) {
                          return sum - (parseFloat(e.amount) || 0)
                        }
                        return sum
                      }, 0)

                      return (
                        <tr key={row.key}
                          style={{ borderBottom:`1px solid ${alpha(T.bd, 0.5)}`,
                            background: row.bold ? T.s2 : 'transparent' }}>
                          <td style={{ padding:'8px 14px', fontSize:'12px',
                            fontWeight: row.bold ? '700' : '400',
                            color: row.bold ? T.tx : T.mt,
                            fontFamily: row.bold ? "'Outfit',sans-serif" : "'Times-Roman','Times',serif" }}>
                            {row.label}
                          </td>
                          <td style={{ textAlign:'right', padding:'8px 12px', fontSize:'12px',
                            fontFamily:"'DM Mono',monospace", color:T.or,
                            fontWeight: row.bold ? '700' : '400' }}>
                            {fmtR(parentDerived[row.key])}
                          </td>
                          {subsidiaries.map(sub => {
                            const subDerived = derivedFigures(entityFigures[sub.client.id] || {})
                            return (
                              <td key={sub.client.id}
                                style={{ textAlign:'right', padding:'8px 12px', fontSize:'12px',
                                  fontFamily:"'DM Mono',monospace", color:T.bl,
                                  fontWeight: row.bold ? '700' : '400' }}>
                                {fmtR(subDerived[row.key])}
                              </td>
                            )
                          })}
                          {eliminations.length > 0 && (
                            <td style={{ textAlign:'right', padding:'8px 12px',
                              fontSize:'12px', fontFamily:"'DM Mono',monospace", color:T.rd }}>
                              {elimEffect !== 0 ? fmtR(elimEffect) : '—'}
                            </td>
                          )}
                          <td style={{ textAlign:'right', padding:'8px 12px', fontSize:'12px',
                            fontFamily:"'DM Mono',monospace",
                            fontWeight: row.bold ? '800' : '600',
                            color: row.bold
                              ? (hasConsVal && consVal < 0 ? T.rd : T.gn)
                              : (hasConsVal ? T.tx : T.mt) }}>
                            {fmtR(consVal)}
                          </td>
                        </tr>
                      )
                    })}

                    {/* Minority interest row */}
                    {minorityInterest > 0 && (
                      <tr style={{ borderTop:`1px solid ${T.bd}`, background:T.yld }}>
                        <td style={{ padding:'8px 14px', fontSize:'12px', color:T.yl,
                          fontWeight:'600' }}>Minority Interest (non-controlling)</td>
                        <td colSpan={1 + subsidiaries.length + (eliminations.length > 0 ? 1 : 0)}/>
                        <td style={{ textAlign:'right', padding:'8px 12px', fontSize:'12px',
                          fontFamily:"'DM Mono',monospace", color:T.yl, fontWeight:'700' }}>
                          {fmtR(minorityInterest)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Generate */}
            {generateError && (
              <div style={{ background:alpha(T.rd, 0.08), border:`1px solid ${alpha(T.rd, 0.2)}`,
                borderRadius:'8px', padding:'12px 16px', marginBottom:'16px',
                fontSize:'14px', color:T.rd, display:'flex', alignItems:'center',
                justifyContent:'space-between', gap:'12px', flexWrap:'wrap' }}>
                <span><Icon name="alert" /> {generateError}</span>
                {/* Payment lock (see is_generation_locked / the pay-per-engagement charge in
                    main.py): a consolidated AFS is final AFS generation, so an unpaid, card-less
                    or declined-card firm now gets a 402 whose text points at Billing. No
                    structured error code exists, so this is matched on wording, like the same
                    shortcut on Statements.jsx -- it only adds a link, never blocks anything.
                    The suspended-account message tells the firm to contact support instead, and
                    deliberately gets no Billing button. */}
                {pointsToBilling(generateError) && (
                  <button onClick={() => navigate('/billing')}
                    style={{ padding:'6px 14px', borderRadius:'6px', border:`1px solid ${T.rd}`,
                      background:'transparent', color:T.rd, fontSize:'12px', fontWeight:'700',
                      cursor:'pointer', whiteSpace:'nowrap' }}>
                    Go to Billing →
                  </button>
                )}
              </div>
            )}

            {/* R4 validation panel (Phase 1, 9 Sep 2026) — appears once the
                backend has actually rejected/flagged the aggregated figures;
                there's no proactive pre-check yet, so this only shows up
                after a first Generate attempt. */}
            {validationChecks && validationChecks.some(c => c.status !== 'pass') && (
              <div style={{
                background: validationHasFail ? alpha(T.rd, 0.08) : alpha(T.am, 0.08),
                border: `1px solid ${validationHasFail ? alpha(T.rd, 0.3) : alpha(T.am, 0.3)}`,
                borderRadius:'8px', padding:'14px 16px', marginBottom:'16px' }}>
                <div style={{ fontSize:'14px', fontWeight:'700',
                  color: validationHasFail ? T.rd : T.yl, marginBottom:'8px' }}>
                  {validationHasFail ? <><Icon name="block" /> Validation failed</> : <><Icon name="alert" /> Validation warnings</>}
                </div>
                <ul style={{ margin:0, paddingLeft:'20px', fontSize:'13px', color:T.mt }}>
                  {validationChecks.filter(c => c.status !== 'pass').map((c, i) => (
                    <li key={i} style={{ marginBottom:'4px' }}>
                      <strong style={{ color:T.tx }}>{c.label}:</strong> {c.detail}
                    </li>
                  ))}
                </ul>
                <label style={{ display:'flex', alignItems:'center', gap:'8px', marginTop:'10px',
                  fontSize:'13px', color:T.tx, cursor:'pointer' }}>
                  <input type="checkbox" checked={validationOverride}
                    onChange={e => setValidationOverride(e.target.checked)} />
                  {validationHasFail
                    ? 'I have reviewed this and want to generate the consolidated AFS anyway'
                    : 'I have reviewed the warning(s) above and want to proceed'}
                </label>
              </div>
            )}

            <div style={{ display:'flex', gap:'12px', alignItems:'center', flexWrap:'wrap' }}>
              <button onClick={handleStepBack}
                style={{ padding:'11px 20px', borderRadius:'8px',
                  border:`1px solid ${T.bd}`, background:'transparent', color:T.mt,
                  fontFamily:"'Outfit',sans-serif", fontSize:'14px', cursor:'pointer' }}>
                ← Edit Eliminations
              </button>
              <button onClick={handleGenerate} disabled={generating || savingGroup || validationGateOpen}
                title={validationGateOpen ? 'Resolve or override the validation issue(s) above first' : undefined}
                style={{ padding:'13px 36px', borderRadius:'8px', border:'none',
                  background: (generating || savingGroup || validationGateOpen)
                    ? T.mt
                    : T.prg,
                  color:T.onpri, fontFamily:"'Outfit',sans-serif",
                  fontSize:'15px', fontWeight:'800',
                  cursor: (generating || savingGroup || validationGateOpen) ? 'not-allowed' : 'pointer',
                  boxShadow: (generating || savingGroup || validationGateOpen) ? 'none' : `0 6px 24px ${alpha(T.pri, 0.35)}`,
                  display:'flex', alignItems:'center', gap:'8px' }}>
                {generating ? (
                  <><span style={{ width:'16px', height:'16px',
                    border:`2px solid ${alpha(T.onpri, 0.3)}`, borderTopColor:T.onpri,
                    borderRadius:'50%', animation:'spin 0.8s linear infinite',
                    display:'inline-block' }}/>Building Consolidated AFS...</>
                ) : savingGroup ? <><Icon name="hourglass" /> Saving...</> : <><Icon name="download" /> Generate Consolidated AFS →</>}
              </button>
            </div>
            <div style={{ marginTop:'10px', fontSize:'12px', color:T.mt, lineHeight:'1.6' }}>
              The PDF downloads immediately. The group is saved for future regeneration.
              Consolidated figures are computed fresh from each entity's most recent AFS job.
            </div>
          </div>
        )}

        {/* ── DONE ──────────────────────────────────────────────────────── */}
        {builderStep === 'done' && (
          <div style={{ textAlign:'center', padding:'48px 20px' }}>
            <div style={{ fontSize:'64px', marginBottom:'20px' }}><Icon name="checkCircle" strokeWidth={1.5} style={{ color: T.gn }} /></div>
            <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'26px',
              fontWeight:'800', marginBottom:'10px' }}>
              Consolidated AFS Generated
            </div>
            <div style={{ fontSize:'14px', color:T.mt, lineHeight:'1.8', marginBottom:'8px' }}>
              The consolidated financial statements for{' '}
              <strong style={{ color:T.tx }}>{doneGroup?.name}</strong>{' '}
              (FY {doneGroup?.year}) have been downloaded.
            </div>
            <div style={{ fontSize:'14px', color:T.mt, marginBottom:'32px' }}>
              {subsidiaries.length + 1} entities consolidated ·{' '}
              {eliminations.length} intercompany elimination{eliminations.length !== 1 ? 's' : ''} applied
              {minorityInterest > 0 && ` · Minority interest ${fmtR(minorityInterest)}`}
            </div>
            {postGenerateWarnings.length > 0 && (
              <div style={{ textAlign:'left', maxWidth:'640px', margin:'0 auto 32px',
                background:alpha(T.am, 0.08), border:`1px solid ${alpha(T.am, 0.3)}`,
                borderRadius:'8px', padding:'14px 16px' }}>
                <div style={{ fontSize:'14px', fontWeight:'700', color:T.yl, marginBottom:'8px' }}>
                  <Icon name="alert" /> Worth reviewing before you send this out
                </div>
                <ul style={{ margin:0, paddingLeft:'20px', fontSize:'13px', color:T.mt }}>
                  {postGenerateWarnings.map((w, i) => (
                    <li key={i} style={{ marginBottom:'6px' }}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
            <div style={{ display:'flex', gap:'12px', justifyContent:'center', flexWrap:'wrap' }}>
              <button onClick={handleGenerate} disabled={generating}
                style={{ padding:'11px 24px', borderRadius:'8px',
                  border:`1px solid ${T.or}`, background:'transparent', color:T.or,
                  fontFamily:"'Outfit',sans-serif", fontSize:'14px',
                  fontWeight:'700', cursor:'pointer' }}>
                Download again
              </button>
              <button onClick={() => setBuilderStep('preview')}
                style={{ padding:'11px 24px', borderRadius:'8px',
                  border:`1px solid ${T.bd}`, background:'transparent', color:T.mt,
                  fontFamily:"'Outfit',sans-serif", fontSize:'14px', cursor:'pointer' }}>
                Edit & Re-generate
              </button>
              <button onClick={() => { setScreen('list'); loadGroups() }}
                style={{ padding:'11px 24px', borderRadius:'8px', border:'none',
                  background:T.prg, color:T.onpri,
                  fontFamily:"'Outfit',sans-serif", fontSize:'14px',
                  fontWeight:'700', cursor:'pointer' }}>
                Back to Groups
              </button>
            </div>
          </div>
        )}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}

